/**
 * Scroll-reveal animations
 * Ported from the sill-dawn theme.
 *
 * - Elements with `.scroll-trigger` animate in (slide-up + fade) as they
 *   enter the viewport. `.animate--slide-in` (default) or `.animate--fade-in`.
 * - Auto-applies the trigger class to each direct section child of
 *   `#MainContent` (`.shopify-section`), so most pages get the effect with
 *   no template edits. Opt out per-section with `data-no-scroll-reveal`.
 * - For row stagger: add `data-cascade` to a parent and the JS will assign
 *   `--animation-order` from the intersection index. For a deterministic
 *   per-item order, set `style="--animation-order: N;"` inline on each child.
 * - Respects `prefers-reduced-motion` (no JS gate needed — CSS handles it).
 * - In Shopify Theme Editor (design mode) animations are disabled to avoid
 *   constant re-trigger as the editor re-renders sections.
 */

const TRIGGER = 'scroll-trigger';
const OFFSCREEN = 'scroll-trigger--offscreen';
const DESIGN_MODE = 'scroll-trigger--design-mode';
const CANCEL = 'scroll-trigger--cancel';

function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const target = entry.target;
    if (entry.isIntersecting) {
      if (target.classList.contains(OFFSCREEN)) {
        target.classList.remove(OFFSCREEN);
        if (target.hasAttribute('data-cascade') && !target.style.getPropertyValue('--animation-order')) {
          target.style.setProperty('--animation-order', String(index));
        }
      }
      observer.unobserve(target);
    } else {
      target.classList.add(OFFSCREEN);
      target.classList.remove(CANCEL);
    }
  });
}

/**
 * Auto-apply the trigger class to top-level sections inside <main>.
 *
 * Skips:
 *   - sections that opt out via `data-no-scroll-reveal`
 *   - sections that are already in the viewport at init time (they render
 *     normally with no animation — prevents the visible "flash" of an
 *     above-the-fold section animating from hidden state on first load)
 *
 * Below-the-fold sections are tagged AND pre-marked as `--offscreen`
 * synchronously, so they start hidden before the observer fires its first
 * callback (no partial animation flicker).
 *
 * @param {ParentNode} root
 */
function autoTagSections(root) {
  const scope = /** @type {Document | Element} */ (root);
  const main =
    (scope.querySelector && scope.querySelector('main#MainContent')) ||
    (scope.querySelector && scope.querySelector('main')) ||
    null;
  if (!main) return;

  const viewportHeight = window.innerHeight;
  const sections = main.querySelectorAll(
    ':scope > .shopify-section:not([data-no-scroll-reveal]):not(.' + TRIGGER + ')'
  );

  sections.forEach((section) => {
    const rect = section.getBoundingClientRect();
    const isInView = rect.top < viewportHeight && rect.bottom > 0;

    if (isInView) return; // already visible — leave it alone, no animation

    section.classList.add(TRIGGER, 'animate--slide-in', OFFSCREEN);
  });
}

/**
 * Initialize the observer for every `.scroll-trigger` in scope.
 *
 * @param {Document | Element} [root=document]
 * @param {boolean} [isDesignModeEvent=false]
 */
function initializeScrollAnimationTrigger(root = document, isDesignModeEvent = false) {
  autoTagSections(root);

  const elements = Array.from(
    typeof root.getElementsByClassName === 'function'
      ? root.getElementsByClassName(TRIGGER)
      : root.querySelectorAll(`.${TRIGGER}`)
  );
  if (elements.length === 0) return;

  if (isDesignModeEvent) {
    elements.forEach((el) => el.classList.add(DESIGN_MODE));
    return;
  }

  // rootMargin bottom: +100px → element is treated as "in view" 100px BEFORE
  // its top edge enters the viewport. The animation gets a head start before
  // the user actually scrolls to the row, so it feels intentional without
  // dragging behind the scroll.
  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px 100px 0px',
  });
  elements.forEach((el) => observer.observe(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeScrollAnimationTrigger());
} else {
  initializeScrollAnimationTrigger();
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
  });
}
